import type { ReactNode } from 'react';

/**
 * Reusable confirmation modal for Business Profile Setup / Company Details — reuses the exact
 * `.bps-modal-backdrop`/`.bps-modal`/`.bps-modal-actions` CSS classes BusinessProfileSetupPage.tsx's own
 * inline Clear Draft confirm already established (business-profile-setup.css), so every confirmation in
 * this feature (Save Changes, Add/Update/Archive/Reactivate Work Location, Send/Revoke Invitation, Owner
 * changes, Discard Admin draft) looks and behaves identically. No app-wide Modal component exists yet (see
 * this feature's own EPN-37 admin-edit-mode correction notes) — this is deliberately feature-local rather
 * than promoted to a shared/ component, to keep this change's blast radius scoped to Business Profile
 * Setup; a shared Modal can be extracted later if another feature needs the same pattern. Never uses
 * `window.confirm` — see CLAUDE.md's UX guidance against native browser dialogs for consequential in-app
 * actions.
 *
 * Visual shape (icon badge, centered copy, centered pill-button pair) matches the approved Business
 * Profile Setup / Company Details Figma reference's own confirmation-prompt screens (e.g. "Discard this
 * Draft?", "Save Before Leaving?") — see business-profile-setup.css's own `.bps-modal*` comment for the
 * exact mapping. No close/dismiss icon and no per-action icon variant exist in that reference, so this
 * component doesn't invent one — every instance uses the same single attention-badge glyph the approved
 * screens do.
 *
 * EPN-37 unsaved-changes correction: the admin-edit "unsaved changes — switch sections?" prompt needs a
 * third action (Stay Here / Discard & Continue / Save & Continue) that the approved reference's own 2-button
 * screens don't show. Rather than invent a separate modal, `tertiaryLabel`/`onTertiary` extend this same
 * component with one more pill button in the same row, keeping the exact icon badge/centered-copy/pill-
 * button visual system for every confirmation in this feature, 2-button or 3-button alike. Omit both props
 * for the standard 2-button flow (every other call site).
 */
export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Adds `bp-btn-clear` (the feature's existing danger-red button style) to the confirm action instead of
   * the default `bp-btn-primary`, for a destructive/irreversible action (e.g. Archive, Discard, Revoke). */
  destructive?: boolean;
  /** Optional third action rendered between Cancel and Confirm (e.g. "Discard & Continue"). Always styled
   * `bp-btn-clear` — this codebase's own established convention for a discard/revoke-shaped action (see
   * AdminInfoStep's Discard Draft/Revoke Invitation, both `destructive: true`) — regardless of `destructive`,
   * which only affects the primary `confirmLabel` button. */
  tertiaryLabel?: string;
  onTertiary?: () => void;
  /** `'success'` swaps the red attention badge for the green check the approved Figma uses for "created successfully" (Step 5, S25). */
  tone?: 'attention' | 'success';
  /** Hides the Cancel button for a single-action notice (the success card has only "Go to Branch List"). Backdrop click still calls `onCancel`. */
  hideCancel?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  tertiaryLabel,
  onTertiary,
  tone = 'attention',
  hideCancel = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;

  return (
    <div className="bps-modal-backdrop" role="presentation" onClick={onCancel}>
      <div className="bps-modal" role="dialog" aria-modal="true" aria-labelledby="bps-confirm-dialog-title" onClick={(e) => e.stopPropagation()}>
        <div className={`bps-modal-icon${tone === 'success' ? ' success' : ''}`} aria-hidden="true">
          <i className={tone === 'success' ? 'fa fa-check' : 'fa fa-exclamation'} />
        </div>
        <h3 id="bps-confirm-dialog-title">{title}</h3>
        <p>{body}</p>
        <div className="bps-modal-actions">
          {!hideCancel && (
            <button type="button" className="bp-btn bp-btn-secondary" onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          {tertiaryLabel && onTertiary && (
            <button type="button" className="bp-btn bp-btn-clear" onClick={onTertiary}>
              {tertiaryLabel}
            </button>
          )}
          <button type="button" className={`bp-btn ${destructive ? 'bp-btn-clear' : 'bp-btn-primary'}`} onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
